import React from 'react';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { RespirationMetricsData, RespirationTimeSeriesData } from '../../types/respirationTimeSeries';
import {
  computeRespirationSummary,
  formatMeasuredDuration,
  RespirationPopulationSummary,
} from '../../utilities/respirationSummary';
import { RESPIRATION_MAX_DISPLAY_BRPM } from '../../utilities/respirationDisplay';

interface RespirationSummaryTableProps {
  data: RespirationTimeSeriesData | null;
  startTime: number; // Timestamp in milliseconds
  /** Analysis-service summary; keeps the all-measured median equal to the summary-table row. */
  summary?: RespirationMetricsData | null;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: 800,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  table: {
    width: 500,
  },
  cell: {
    border: 'none',
  },
  title: {
    paddingBottom: theme.spacing(1),
  },
  groupCell: {
    border: 'none',
    fontWeight: theme.typography.fontWeightBold as number,
    paddingTop: theme.spacing(1.5),
  },
  note: {
    marginTop: theme.spacing(1),
    display: 'block',
  },
}));

/** Same one-decimal rendering as the summary-table row ("21.0", not "21"). */
const oneDecimal = (value: number) => value.toFixed(1);

type Row = { kind: 'group'; name: string } | { kind: 'value'; name: string; value: string };

/**
 * Respiration summary table.
 *
 * Replaces the time-series chart in the report (project_management#540). Shows
 * two medians side by side (project_management#549) — over all measured
 * minutes, and over minutes within the validated range — each with its own
 * range and coverage, so the two numbers can be told apart and reconciled.
 * The all-measured median matches the summary-table row above it.
 *
 * Renders nothing when no windows qualify — the parent already gates on the
 * series existing, and this keeps the no-phantom-card behaviour for sequences
 * where respiration resolved zero confident windows.
 */
export const RespirationSummaryTable: React.FC<RespirationSummaryTableProps> = ({
  data,
  startTime,
  summary: serverSummary,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const summary = computeRespirationSummary(data, startTime, serverSummary);

  if (!summary) {
    return null;
  }

  const populationRows = (population: RespirationPopulationSummary | null): Row[] => [
    {
      kind: 'value',
      name: t('respirationTable.median'),
      value: population
        ? t('respirationTable.brpmValue', { value: oneDecimal(population.medianBrpm) })
        : '—',
    },
    {
      kind: 'value',
      name: t('respirationTable.typicalRange'),
      value: population
        ? t('respirationTable.rangeValue', {
          low: oneDecimal(population.rangeBrpm.low),
          high: oneDecimal(population.rangeBrpm.high),
        })
        : '—',
    },
    {
      kind: 'value',
      name: t('respirationTable.measuredTime'),
      value: t('respirationTable.measuredTimeValue', {
        duration: formatMeasuredDuration(population?.minutes ?? 0),
        percent: population?.coveragePercent ?? 0,
      }),
    },
  ];

  const rows: Row[] = [
    { kind: 'group', name: t('respirationTable.allMeasured') },
    ...populationRows(summary.allMeasured),
    {
      kind: 'group',
      name: t('respirationTable.validatedRange', { max: RESPIRATION_MAX_DISPLAY_BRPM }),
    },
    ...populationRows(summary.validatedRange),
  ];

  if (summary.nightMedianBrpm !== null) {
    rows.push({
      kind: 'value',
      name: t('respirationTable.nightMedian'),
      value: t('respirationTable.brpmValue', { value: oneDecimal(summary.nightMedianBrpm) }),
    });
  }

  return (
    <Container className={classes.root} component={Paper} data-testid="respiration-summary-table">
      <Typography className={classes.title} variant="h6">
        {t('respirationTable.title')}
      </Typography>
      <Table className={classes.table} size="small">
        <TableBody>
          {rows.map((row, index) => (
            // Row names repeat across the two groups, so the index is part of the key.
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={`${index}-${row.name}`}>
              {row.kind === 'group' ? (
                <TableCell className={classes.groupCell} colSpan={2}>
                  {row.name}
                </TableCell>
              ) : (
                <>
                  <TableCell className={classes.cell}>{row.name}</TableCell>
                  <TableCell className={classes.cell} align="right">
                    {row.value}
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography className={classes.note} variant="caption" color="textSecondary">
        {t('respirationTable.note', { max: RESPIRATION_MAX_DISPLAY_BRPM })}
      </Typography>
    </Container>
  );
};
